import React, { useState } from 'react';
import {
  buildGenericVehicleImageUrl,
  buildStockHeroImageUrl,
  VehicleImageVariant
} from '|/helpers/vehicle-image';
import styles from './vehicle-image.module.css';

type Widths = number | Record<VehicleImageVariant, number>;

interface VehicleImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width'> {
  stockNumber?: number | string | null;
  /** 2x the CSS box. Both variants only where the box changes across the 768px breakpoint. */
  width: Widths;
  /** Silhouette shape when it does not follow the breakpoint. Defaults to the variant rendered. */
  variant?: VehicleImageVariant;
  alt: string;
}

const widthFor = (width: Widths, variant: VehicleImageVariant) =>
  typeof width === 'number' ? width : width[variant];

/**
 * Every vehicle photo in the funnel.
 *
 * Failure must be state, not a DOM mutation: React attaches `error` via `addEventListener`, so
 * clearing `currentTarget.onerror` does not detach its own handler, and reassigning `src`
 * refetches — a silhouette that also fails would loop forever.
 */
const VehicleImage = ({ stockNumber, width, variant, alt, ...imgProps }: VehicleImageProps) => {
  const [failure, setFailure] = useState({ stockNumber, failed: false });

  // An effect would commit one render still marked failed, requesting the silhouette for a
  // vehicle that never failed.
  if (failure.stockNumber !== stockNumber) {
    setFailure({ stockNumber, failed: false });
  }

  const hasFailed = failure.stockNumber === stockNumber && failure.failed;

  const srcFor = (forVariant: VehicleImageVariant) => {
    const forWidth = widthFor(width, forVariant);
    const silhouette =
      buildGenericVehicleImageUrl({ variant: variant ?? forVariant, width: forWidth }) ?? '';

    if (hasFailed) return silhouette;

    return buildStockHeroImageUrl({ stockNumber, width: forWidth }) ?? silhouette;
  };

  const image = (
    <img
      {...imgProps}
      src={srcFor('mobile')}
      alt={alt}
      onError={() => {
        setFailure({ stockNumber, failed: true });
      }}
    />
  );

  if (typeof width === 'number') return image;

  return (
    <picture className={styles.picture}>
      <source media="(min-width: 768px)" srcSet={srcFor('desktop')} />
      {image}
    </picture>
  );
};

export default VehicleImage;
